import React, { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Container } from 'reactstrap';
import { skipToken } from '@reduxjs/toolkit/query';

import { faExclamationTriangle } from '~components/icon';
import { TableView } from '~components/table-view-v3';

import getRejectedColumns from '~weekly-planner/forms/rejected/list';

import { useGetRejectedQuery } from '~weekly-planner/actions/appointments/rejected';

import { clear, updateParams } from '~weekly-planner/reducers/appointments/rejected';

import { selectDerivedWeekRange, selectShowSystemUsers } from '~weekly-planner/selectors';
import { selectAll, selectCount, selectParams } from '~weekly-planner/selectors/appointments/rejected';

interface ComponentProps {
  isOpen: boolean;
}

const Rejected: React.FC<ComponentProps> = React.memo(({ isOpen }) => {
  const dispatch = useAppDispatch();
  const params = useAppSelector(selectParams);
  const rejectedAppointments = useAppSelector(selectAll);
  const count = useAppSelector(selectCount);
  const showSystemUsers = useAppSelector(selectShowSystemUsers);
  const showSystemUsersParam = showSystemUsers ? { show_system_users: true } : {};

  const derivedWeekRange = useAppSelector(selectDerivedWeekRange);
  const { week_start } = derivedWeekRange;

  const { limit } = params;

  // RTK Query Args
  const queryArgs = useMemo(
    () => ({
      limit,
      week_start: params?.week_start,
      ...showSystemUsersParam,
    }),
    [params, showSystemUsers],
  );

  // RTK Query Hook
  const { isFetching } = useGetRejectedQuery(params?.week_start && isOpen ? queryArgs : skipToken, {
    refetchOnMountOrArgChange: true,
  });

  const onLoadMore = () => {
    const newLimit = limit + 30;
    dispatch(updateParams({ ...params, limit: newLimit }));
  };

  useEffect(() => {
    if (isOpen && week_start) {
      dispatch(
        updateParams({
          week_start,
          limit: 30,
          ...showSystemUsersParam,
        }),
      );
    }
  }, [isOpen, week_start, showSystemUsers]);

  // unmount & clear reducer
  useEffect(() => {
    return () => {
      dispatch(clear([]));
    };
  }, []);

  const rejectedColumns = getRejectedColumns();

  // Memoise the mapped data so id-less rows keep a stable randomUUID across renders
  const tableData = useMemo(
    () =>
      rejectedAppointments.map((appt) => ({
        ...appt,
        id: appt.id?.toString() ?? crypto.randomUUID(),
      })),
    [rejectedAppointments],
  );

  return (
    <div className="rejected-list">
      <Container className="inner-content limited">
        <TableView
          id="rejected-appointments"
          data={tableData}
          columns={rejectedColumns}
          readonly
          striped
          isLoading={isFetching}
          allowPagination={true}
          onLoadMore={onLoadMore}
          totalCount={count}
          emptyIcon={faExclamationTriangle}
          emptyMessage="No rejected appointments found"
        />
      </Container>
    </div>
  );
});

Rejected.displayName = 'Rejected';
export default Rejected;
